<template>
  <div class="app-bg tw-font-sans tw-relative">
    <template v-if="!loading">
      <MenuNav>
        <div class="tw-h-8"></div>
        <slot name="side-nav" />
      </MenuNav>
      <RightSideNav />
    </template>
    <portal to="right-side-nav" :disabled="!showNotifications">
      <transition name="fade-delay" mode="out-in">
        <NotificationList v-if="showNotifications" />
      </transition>
    </portal>
    <portal to="right-side-nav" :disabled="!showTaskDetails">
      <transition name="fade-delay" mode="out-in">
        <TaskDetails v-if="showTaskDetails" :task="task" class="tw-w-full" />
      </transition>
    </portal>
    <portal to="right-side-nav" />
    <transition :name="isMobileView ? 'new' : 'fade'" mode="out-in">
      <PopOver v-if="popOverIsOpen" />
    </transition>
    <transition name="fade" mode="out-in">
      <BaseModal />
    </transition>
    <div v-if="appError || xhrError">
      <div class="app-view">
        <p class="tw-text-center tw-py-10 tw-text-red-500" v-html="appError || xhrError"></p>
      </div>
    </div>
    <div v-else-if="loading">
      <div class="app-view">
        <p class="tw-text-center tw-py-10 opacity-31 tw-py-48">Loading...</p>
      </div>
    </div>
    <transition v-else :name="transitionName" :mode="transitionMode">
      <router-view />
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import MenuNav from '@/views/_partials/MenuNav.vue';
import RightSideNav from '@/views/_partials/RightSideNav.vue';
import TaskDetails from '@/views/_partials/TaskDetails.vue';
import NotificationList from '@/components/NotificationList.vue';
import PopOver from '@/components/PopOver.vue';
import BaseModal from '@/components/BaseModal.vue';
import { apiStore } from '@/modules/apiHelper';
import userApp from '@/mixins/userApp';

const appErrorMessage = 'Sorry, an error occured. Check your internet connection.';

export default {
  name: 'UserApp',
  components: {
    MenuNav,
    RightSideNav,
    NotificationList,
    TaskDetails,
    PopOver,
    BaseModal,
  },
  mixins: [userApp],
  props: {
    transitionName: {
      type: String,
      default: 'fade',
    },
    transitionMode: {
      type: String,
      default: 'in-out',
    },
    userType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      appError: undefined,
      scope: undefined,
    };
  },
  computed: {
    ...mapState({
      showNotifications: state => state.ui.showNotifications,
      showTaskDetails: state => state.ui.showTaskDetails,
      task: state => state.ui.currentTaskDetails,
      xhrError: state => state.ui.xhrError,
    }),
    ...mapGetters([
      'popOverIsOpen',
    ]),
  },
  methods: {
    async getUiSettings() {
      await this.$store.dispatch('getUiSettings', true);
    },
    // checks if user's information is correct.
    // else redirects to the right view
    async checkUserInfo() {
      const currentUser = await this.$store.dispatch('userIsNew');

      if (!currentUser[0]) {
        await this.logout();
        return false;
      }

      console.log(`hello ${this.$store.state.User.name}`, '\n\n---------------');

      await this.$nextTick();
      await this.$nextTick();

      const isMainApp = this.scope[1];

      // * NEW USER
      if (
        currentUser[1] === false
        || currentUser[2] === false
        || (this.isMentor && currentUser[4] === false && isMainApp) // course
        || (this.isMentee && currentUser[5] === false && isMainApp) // package.
      ) {
        if (this.$route.name !== 'NewUser') {
          // redirect to orientation page!
          this.$router.push({
            name: 'NewUser',
          });
        }

        return true;
      }

      // * check COC
      if (currentUser[3] === false) {
        if (this.$route.name !== 'app') {
          this.$router.push({
            name: 'app',
          });
        }

        return true;
      }

      if (this.isMentee && isMainApp) {
        const isRedirected = await this.checkMenteePackage();
        await this.$nextTick();

        if (isRedirected) {
          return true;
        }
      }

      if (
        this.isMentee
        && this.$route.name !== 'requestMentor'
      ) {
        await this.checkMenteeMentors();
        await this.$nextTick();
      }

      return true;
    },
    // check if mentee has mentors,
    // else automate finding mentors
    async checkMenteeMentors() {
      await this.$store.dispatch('getMyMentors', true);
      await this.$nextTick();
      await this.$nextTick();

      const myMentors = this.$store.state.User.mentee.mentors;
      if (myMentors.length === 0) {
        await this.$store.dispatch('findMentors', {});

        if (this.$route.name !== 'requestedMentorsList' && this.$route.name !== 'settings') {
          this.$router.push({
            name: 'requestedMentorsList',
          });

          await this.$nextTick();

          this.$toasted.info('Find yourself a mentor!', {
            duration: 5000,
            position: 'bottom-center',
          });
        }

        return false;
      }
      return true;
    },
    // check if package is valid
    // else redirect to profile view
    async checkMenteePackage() {
      await this.$store.dispatch('getMenteePackage', true);
      // everything is stored in the vuex, now validating

      if (this.isMentee && (!this.menteePackageIsValid || this.menteePackageIsExpired)) {
        console.warn('package not valid or has expired. redirecting...');

        if (this.$route.name !== 'license') {
          this.$router.push({
            name: 'license',
          });

          return true;
        }
      }

      return false;
    },
  },
  async created() {
    await this.$nextTick();
    this.$store.commit('xhrError');
    this.$store.commit('showMenuNav', false);

    await this.$nextTick();
    this.scope = await this.$store.dispatch('evaluateScope');

    return this.checkUserInfo()
      .catch((err) => {
        console.warn('userApp err: ', err);
        this.$toasted.global.appError();
        this.appError = appErrorMessage;
        return false;
      })
      .then(async (res) => {
        // console.info('user info status', !!res);
        if (!res) {
          await this.logout('You need to log in.');
          return false;
        }

        await this.getUiSettings();

        this.loading = false;
        this.appError = undefined;
        this.$store.dispatch('getUserData');
        this.$store.dispatch('getNotifications');
        return true;
      });
  },
  async beforeRouteLeave(to, from, next) {
    this.$store.commit('closeRightSideNav');
    this.$store.commit('showMenuNav', false);
    this.$store.commit('openPopOver', [false]);
    this.clearOnResizeFns();

    console.log(this.menteePackageIsExpired, to.name !== 'license');

    const currentUserGoals = await this.$store.state.User[this.userType].goals;
    const userBio = await apiStore.getItem('userbio') || '';
    const userPhoto = await apiStore.getItem('userphoto') || '';
    if (
      (currentUserGoals.length < 1 && to.name !== 'settings')
      || ((userBio.length === 0 || userPhoto.length === 0) && to.name !== 'profile')
      || (this.menteePackageIsExpired && to.name !== 'license')
    ) {
      await this.checkUserInfo()
        .catch((err) => {
          console.warn(err);
          this.$toasted.global.appError();
          this.appError = appErrorMessage;
          return false;
        });
    }

    next();
  },
};
</script>

<style scoped>
</style>
